<template>
  <FormSection
    :title="$t('form.invoiceForm.headers.buyer_v2')"
    :description="getTranslation('form.invoiceForm.headers.buyer_description')"
    :image="require('~/images/ico-subscribe.svg')"
    :custom-class="isPolish ? 'column-wrapper' : ''"
    class="el-form-item--lg"
  >
    <FormGroup
      :input-value="buyer_company_name"
      :label="$t('form.invoiceForm.fields.buyer_history_list.label')"
      :tooltip-content="$t('form.invoiceForm.fields.buyer_history_list.placeholder')"
      class="stored-contacts"
      data-test="buyer_company_name"
      floating-label
      has-force-focus
      prop="buyer_company_name"
      spacing="sm"
      v-if="hasAnyContacts"
    >
      <Select
        :no-data-text="$t('form.invoiceForm.fields.buyer_history_list.no_data_text')"
        :popper-append-to-body="false"
        :remote-method="fetchSuggestions"
        @change="fetchContactData"
        @visible-change="resetFilter"
        autocomplete="new-password"
        class="select-v2"
        clearable
        default-first-option
        filterable
        name="buyer_company_name"
        remote
        v-model="buyer_company_name"
      >
        <Option
          v-for="option in buyersSuggestions"
          :key="option.id"
          :label="option.name"
          :value="option.id"
        />
      </Select>
    </FormGroup>

    <CompanyInfoInput
      prefix="buyer"
      ref="companyLookup"
      v-if="visibilities.buyer_company_number"
      class="company-number"
      :optional="$t('form.invoiceForm.fields.buyer_company_number.optional')"
    />

    <FormGroup
      spacing="sm"
      prop="buyer_name"
      :label="$t('form.invoiceForm.fields.buyer_name.label')"
      floating-label
      :input-value="buyer_name"
      :tooltip-content="$t('form.invoiceForm.fields.buyer_name.placeholder')"
    >
      <Autocomplete
        v-if="usingAresForName"
        data-test="buyer_name"
        name="invoice_attributes_name"
        v-model="buyer_name"
        :fetch-suggestions="fetchSuggestionsByName"
        class="name-autocomplete"
        @select="setData"
        autocomplete="new-password"
        popper-class="name-autocomplete-popper"
        :trigger-on-focus="false"
        :debounce="1000"
      >
        <template slot-scope="{ item }">
          <div class="autocomplete-item">
            <strong>{{ item.name }}</strong>
            <br>
            <span>{{ $t('contacts.fields.company_number.label') }}:
              {{ item.reg_no }}</span>
            <br>
            <span>{{ formattedAddress(item) }}</span>
            <br>
            <small>{{ formattedContacts(item) }}</small>
          </div>
        </template>
      </Autocomplete>

      <TextInput
        name="buyer_name"
        data-test="buyer_name"
        type="string"
        v-model="buyer_name"
        id="invoice_buyer_attributes_name"
        v-if="!usingAresForName"
      />
    </FormGroup>

    <CompanyInfoInput
      prefix="buyer"
      field="tax_number"
      class="tax_number"
      :placeholder="visibilities.buyer_tax_number_placeholder ? $t('form.invoiceForm.fields.buyer_tax_number.placeholder') : ''"
      :optional="$t('form.invoiceForm.fields.buyer_tax_number.optional')"
    />

    <FormGroup
      spacing="sm"
      prop="buyer_vat_number"
      :label="$t('form.invoiceForm.fields.buyer_vat_number.label')"
      v-if="visibilities.buyer_vat_number"
      floating-label
      :input-value="buyer_vat_number"
    >
      <TextInput
        name="buyer_vat_number"
        data-test="buyer_vat_number"
        type="string"
        v-model="buyer_vat_number"
        id="invoice_buyer_attributes_vat_number"
        :placeholder="$t('form.invoiceForm.fields.buyer_vat_number.placeholder')"
      />
    </FormGroup>

    <FormGroup
      spacing="sm"
      prop="buyer_address_street"
      :label="$t('form.invoiceForm.fields.buyer_address_street.label')"
      floating-label
      :input-value="buyer_address_street"
    >
      <TextInput
        name="buyer_address_street"
        data-test="buyer_address_street"
        type="string"
        v-model="buyer_address_street"
        id="invoice_buyer_attributes_address_attributes_street"
        :placeholder="$t('form.invoiceForm.fields.buyer_address_street.placeholder')"
      />
    </FormGroup>

    <FormGroup
      spacing="sm"
      prop="buyer_address_city"
      :label="$t('form.invoiceForm.fields.buyer_address_city.label')"
      floating-label
      :input-value="buyer_address_city"
    >
      <TextInput
        name="buyer_address_city"
        data-test="buyer_address_city"
        type="string"
        v-model="buyer_address_city"
        id="invoice_buyer_attributes_address_attributes_city"
        :placeholder="$t('form.invoiceForm.fields.buyer_address_city.placeholder')"
      />
    </FormGroup>

    <FormGroup
      spacing="sm"
      prop="buyer_address_postcode"
      :label="$t('form.invoiceForm.fields.buyer_address_postcode.label')"
      floating-label
      :input-value="buyer_address_postcode"
    >
      <TextInput
        name="buyer_address_postcode"
        data-test="buyer_address_postcode"
        type="string"
        v-model="buyer_address_postcode"
        id="invoice_buyer_attributes_address_attributes_postcode"
        :placeholder="$t('form.invoiceForm.fields.buyer_address_postcode.placeholder')"
      />
    </FormGroup>

    <CountryField
      v-model="buyer_address_country_code"
      :subdivision="buyer_address_subdivision"
      @update:subdivision="buyer_address_subdivision = $event"
      prefix="buyer"
      floating-label
    />

    <div :class="`toggle-wrapper ${showBuyerContactInfo ? 'is-visible' : 'is-hidden'}`">
      <Anchor
        @click="toggle"
        class="toggle-link"
        data-test="toggle_buyer_contact_info"
      >
        {{ $t(`form.invoiceForm.${showBuyerContactInfo ? 'hide' : 'show'}BuyerContactInfo`) }}
        <i :class="`toggle-icon el-icon-view el-icon-arrow-${showBuyerContactInfo ? 'up' : 'down'}`" />
      </Anchor>

      <CollapseTransition>
        <div v-show="showBuyerContactInfo">
          <FormGroup
            spacing="sm"
            prop="buyer_email"
            :label="$t('form.invoiceForm.fields.buyer_email.label')"
            floating-label
            :input-value="buyer_email"
          >
            <TextInput
              name="buyer_email"
              data-test="buyer_email"
              type="string"
              v-model="buyer_email"
              id="invoice_buyer_attributes_email"
            />
          </FormGroup>

          <FormGroup
            spacing="sm"
            prop="buyer_phone"
            :label="$t('form.invoiceForm.fields.buyer_phone.label')"
            floating-label
            :input-value="buyer_phone"
            class="buyer-phone"
          >
            <TelInput
              v-model="buyer_phone"
              data-test="buyer_phone"
              :default-country="buyer_address_country_code"
              id="invoice_buyer_attributes_phone"
              @update:validity="handlePhoneValidity"
            />
          </FormGroup>

          <FormGroup
            spacing="sm"
            prop="buyer_web"
            :label="$t('form.invoiceForm.fields.buyer_web.label')"
            floating-label
            :input-value="buyer_web"
          >
            <TextInput
              name="buyer_web"
              data-test="buyer_web"
              type="string"
              v-model="buyer_web"
              id="invoice_buyer_attributes_web"
            />
          </FormGroup>
        </div>
      </CollapseTransition>
    </div>
  </FormSection>
</template>

<script>
import { includes } from 'ramda';
import { mapFields } from 'vuex-map-fields';
import { mapState, mapGetters } from 'vuex';
import Cookies from 'js-cookie';
import { COUNTRY_CODES, NAME_LOOKUP_ENABLED_COUNTRIES } from '~/config/constants';

export default {
  components: {
    CompanyInfoInput: () => import('../inputs/CompanyNumber.vue'),
    TelInput: () => import('../inputs/TelInput.vue'),
  },
  data: () => ({
    loading: false,
    buyersSuggestions: [],
    showBuyerContactInfo: false,
    isContactSearching: false,
  }),
  computed: {
    ...mapFields('invoiceForm', [
      'buyer_name',
      'buyer_tax_number',
      'buyer_vat_number',
      'buyer_address_street',
      'buyer_address_city',
      'buyer_address_postcode',
      'buyer_address_country_code',
      'buyer_address_subdivision',
      'buyer_company_number',
      'buyer_company_name',
      'buyer_email',
      'buyer_phone',
      'buyer_web',
    ]),
    ...mapGetters('auth', ['isAuthorized']),
    ...mapState('invoiceForm', ['invoice', 'visibilities']),
    usingAresForName() {
      let countryCode = this.buyer_address_country_code;
      if (!countryCode) {
        countryCode = COUNTRY_CODES[this.$i18n.locale.toUpperCase()];
      }

      return includes(countryCode, NAME_LOOKUP_ENABLED_COUNTRIES);
    },
    isPolish() {
      return COUNTRY_CODES[this.$i18n.locale.toUpperCase()] === COUNTRY_CODES.PL;
    },
    postcodeInputType() {
      const countryCode = COUNTRY_CODES[this.$i18n.locale.toUpperCase()];
      return (countryCode === 'CZ' || countryCode === 'SK') ? 'tel' : 'text';
    },
    hasAnyContacts() {
      return this.buyersSuggestions.length > 0 || this.isContactSearching;
    },

  },
  methods: {
    handlePhoneValidity(isValid) {
      this.$store.dispatch('invoiceForm/updatePhoneValidations', {
        name: 'buyer',
        value: isValid,
      });
    },
    toggle() {
      this.showBuyerContactInfo = !this.showBuyerContactInfo;
      Cookies.set('showBuyerContactInfo', this.showBuyerContactInfo);
    },
    fetchContactData(value) {
      this.$store.dispatch('invoiceForm/fetchContactData', value);
    },
    fetchSuggestions(query) {
      if (query) {
        this.isContactSearching = true;
      }

      if (this.isAuthorized) {
        this.$store.dispatch('contacts/searchForForm', query)
          .then((items) => {
            this.buyersSuggestions = items.collection;
          })
          .catch(() => {
            this.buyersSuggestions = [];
          });
      }
    },
    fetchSuggestionsByName(query, cb) {
      if (this.usingAresForName) {
        if (query !== '') {
          this.$store
            .dispatch('contacts/search', {
              query,
              countryCode: this.buyer_address_country_code.toLowerCase(),
              searchBy: 'name',
            })
            .then((items) => {
              cb(items);
            })
            .catch(() => {
              cb([]);
            });
        }
      }
    },
    setData(data) {
      this.buyer_name = data.name;
      this.buyer_tax_number = data.vat_no;
      this.buyer_vat_number = data.tax_no;
      this.buyer_company_number = data.reg_no;
      this.buyer_address_street = data.address_street;
      this.buyer_address_city = data.address_city;
      this.buyer_address_postcode = data.address_postcode;
      this.buyer_email = data.email;
      this.buyer_web = data.web;
      this.buyer_phone = data.phone;
    },
    formattedContacts(option) {
      return [option.email, option.phone, option.mobile]
        .filter(Boolean)
        .join(', ');
    },
    formattedAddress(option) {
      return [option.address_street, option.address_postcode]
        .filter(Boolean)
        .join(', ');
    },
    getTranslation(key) {
      const translation = this.$t(key);
      return translation !== key ? translation : '';
    },
    resetFilter(visible) {
      if (visible === true) {
        this.fetchSuggestions();
      }
    },
  },
  watch: {
    buyer_address_country_code() {
      this.$store.dispatch('invoiceForm/computeVisibilities');
      this.$root.$emit('getCompanyLookupData', 'buyer');
    },
  },
  mounted() {
    this.$store.subscribe((mutation) => {
      if (mutation.type === 'auth/signInSuccess') {
        this.fetchSuggestions();
      }
    });

    this.fetchSuggestions();
  },
  created() {
    const showBuyerContactInfo = Cookies.get('showBuyerContactInfo');
    if (typeof showBuyerContactInfo !== 'undefined') {
      this.showBuyerContactInfo = showBuyerContactInfo === 'true';
    }
  },
};
</script>

<style lang="scss" scoped>
:deep() {
  .column-wrapper {
    display: flex;
    flex-flow: column wrap;

    .el-form-item {
      @include media('<sm') {
        max-height: inherit;
      }
    }
  }

  .company-number {
    order: -1;
  }

  .tax_number {
    order: -2;
  }

  .stored-contacts {
    order: -3;
  }
}

.autocomplete-item {
  line-height: 100%;
}

.name-autocomplete {
  width: 100%;
}

.toggle-link {
  font-size: rem(14px);
  font-weight: 600;
  display: inline-block;
  width: 100%;
  text-decoration: underline;
  margin-bottom: 1.429rem;
  text-align: right;

  .toggle-icon {
    font-weight: 700;
  }
}

.toggle-wrapper {
  margin-bottom: rem(40px);
  &.is-hidden {
    margin-bottom: rem(20px);
  }
}

.buyer-phone {
  :deep() {
    .el-input__label {
      left: 130px;
    }
  }
}
</style>
